import { useI18n } from '../../hooks/i18n-hooks';
import { useMemo } from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import { numberWithCommas } from '../../functions';
import Skeleton from '@material-ui/lab/Skeleton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import DonatedSoFar from './DonatedSoFar';
import SoldAndDondatedText from './SoldAndDonatedText';

interface SoldAndRaisedBannerProps {
	entity: string;
	kitsSoldTop?: number | undefined;
	kitsSoldBottom?: number | undefined;
	goalKits?: number | undefined;
	sumDonated?: number | undefined;
	dontShowDonated?: boolean;
	loading: boolean;
}

const SoldAndRaisedBanner: React.FC<SoldAndRaisedBannerProps> = ({ entity, kitsSoldTop, kitsSoldBottom, goalKits, sumDonated, dontShowDonated, loading }) => {

	const b = useI18n(i18n => i18n.am.banner);
	const classes = useStyles();

	const isMobile = useMediaQuery('(max-width: 800px)');

	const kitsSold = useMemo(() => kitsSoldTop ?? kitsSoldBottom, [kitsSoldTop, kitsSoldBottom]);
	const goalKitsNumber = useMemo(() => numberWithCommas(goalKits ?? 0), [goalKits]);
	const donatedSoFar = useMemo(() => numberWithCommas(sumDonated ?? 0), [sumDonated]);

	const percentDonated = useMemo(() => {
		if (!kitsSold) return 0;

		const precent = kitsSold * 100 / (goalKits ?? 0);
		return precent > 100 ? 100 : precent;
	}, [goalKits, kitsSold]);

	const percentDonatedNumber = useMemo(() => isNaN(percentDonated) ? 0 : Math.round(percentDonated), [percentDonated]);

	if (!loading && !goalKits) return null;

	return <div className='sold-banner'>
		{!loading && kitsSold !== undefined ? <>
			{kitsSoldTop !== undefined && <SoldAndDondatedText
				kitsSold={kitsSold}
				entity={entity}
				goalKitsNumber={goalKitsNumber}
				donatedSoFar={donatedSoFar}
				dontShowDonated={dontShowDonated}
			/>}
			<LinearProgress className='progress' variant="determinate" color='secondary' value={percentDonatedNumber} />

			<div className='under-text'>
				<div>{percentDonatedNumber}% {b.donatedOfGoal}</div>
				{isMobile && !dontShowDonated && <DonatedSoFar donatedSoFar={donatedSoFar} />}
			</div>

			{kitsSoldBottom && <SoldAndDondatedText
				kitsSold={kitsSold}
				entity={entity}
				goalKitsNumber={goalKitsNumber}
				donatedSoFar={donatedSoFar}
				bottom
			/>}
		</> : loading ? <>

			<Skeleton variant='text' className={classes.header} />

			<div className={clsx("out-of-donated", classes.noMargin)}>
				<Skeleton variant='text' className={classes.text} />
				{!dontShowDonated &&
					<Skeleton variant='text' className={clsx(classes.text, 'donated-so-far')} />}
			</div>

			<Skeleton variant='text' className={classes.bar} />

			<Skeleton variant='text' className={classes.text} />

		</> : null}

	</div>
}

export default SoldAndRaisedBanner;

const useStyles = makeStyles({
	header: {
		width: '40%',
		height: '2.3em',
		marginBottom: -6
	},
	noMargin: {
		margin: 0
	},
	text: {
		width: '28%',
		height: '1.6em !important',
	},
	bar: {
		width: '100%',
		height: '1.6em',
	}
});